import { css } from 'aphrodite';
import * as React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAdminAccountQuery } from '../../../../queries/Admin/Account/useAdminAccountQuery';
import {
	useChargifyCredentialsQuery,
	useCreateSubscriptionMutation,
	usePricebookItemQuery,
} from '../../../../queries/Admin/Pricebook';
import { Button } from '../../../../web/components-v2/Button';
import { Input } from '../../../../web/components-v2/Input';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { useChargifyForm } from '../../../../web/hooks/useChargifyForm';
import { formatCurrency } from '../../../../web/utils/formatCurrency';
import { calculatePricingBreakdown } from '../../../../web/utils/pricingCalculator';
import { PaymentMethod, TaxEstimateData } from '../PricingWizard/types';
import { TaxEstimateDisplay } from '../TaxEstimateDisplay';
import { TaxEstimateModal } from '../TaxEstimateModal';
import { styleSheet } from './styles';

// In-memory storage for passing data to confirmation page (hash history doesn't support state)
// Using module-level variable instead of sessionStorage to avoid exposing sensitive data in browser storage
interface CollectPaymentResultData {
	result: unknown;
	accountId?: string;
}

let collectPaymentResultCache: CollectPaymentResultData | null = null;

export function setCollectPaymentResult(data: CollectPaymentResultData): void {
	collectPaymentResultCache = data;
}

export function getCollectPaymentResult(): CollectPaymentResultData | null {
	const data = collectPaymentResultCache;
	// Clear after reading to prevent stale data
	collectPaymentResultCache = null;
	return data;
}

interface RouteParams {
	pricebookItemId: string;
	accountId?: string;
}

export function CollectPayment() {
	const { pricebookItemId, accountId } = useParams<RouteParams>();
	const location = useLocation();
	const history = useHistory();

	// Check if invoice payment is allowed via query param
	const searchParams = new URLSearchParams(location.search);
	const allowInvoice = searchParams.get('allowInvoice') === 'true';

	// Parse tax data from query string if present (passed from PricingWizard)
	const initialTaxEstimate = React.useMemo<TaxEstimateData | null>(() => {
		const taxParam = searchParams.get('tax');
		const taxAddrParam = searchParams.get('taxAddr');
		if (taxParam && taxAddrParam) {
			try {
				const addr = JSON.parse(taxAddrParam);
				return {
					taxAmount: parseFloat(taxParam),
					address: addr.address,
					address2: addr.address2,
					city: addr.city,
					state: addr.state,
					zip: addr.zip,
				};
			} catch {
				return null;
			}
		}
		return null;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Form state
	const [email, setEmail] = React.useState('');
	const [firstName, setFirstName] = React.useState('');
	const [lastName, setLastName] = React.useState('');
	const [companyName, setCompanyName] = React.useState('');
	const [paymentMethod, setPaymentMethod] = React.useState<PaymentMethod>('CreditCard');

	// Tax estimate state
	const [taxEstimate, setTaxEstimate] = React.useState<TaxEstimateData | null>(initialTaxEstimate);
	const [isTaxModalOpen, setIsTaxModalOpen] = React.useState(false);

	// API queries
	const accountQuery = useAdminAccountQuery({
		accountId: accountId || '',
		enabled: !!accountId,
	});
	const pricebookItemQuery = usePricebookItemQuery({ pricebookItemId: pricebookItemId || '' });
	const chargifyCredentialsQuery = useChargifyCredentialsQuery({
		enabled: paymentMethod === 'CreditCard',
	});
	const createSubscriptionMutation = useCreateSubscriptionMutation();

	// Step state: 'customer' or 'payment'
	const [step, setStep] = React.useState<'customer' | 'payment'>('customer');

	// Chargify form hook - only enabled when on payment step
	const chargifyForm = useChargifyForm({
		credentials: chargifyCredentialsQuery.data,
		enabled: step === 'payment' && paymentMethod === 'CreditCard' && !!chargifyCredentialsQuery.data,
		firstName,
		lastName,
	});

	// Pre-populate company name from account data when available
	React.useEffect(() => {
		if (accountQuery.data?.companyName && !companyName) {
			setCompanyName(accountQuery.data.companyName);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accountQuery.data?.companyName]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		// Validation
		if (!email.trim()) {
			toast.error('Please enter an email address');
			return;
		}
		if (!firstName.trim()) {
			toast.error('Please enter a first name');
			return;
		}
		if (!lastName.trim()) {
			toast.error('Please enter a last name');
			return;
		}

		try {
			let chargifyToken: string | undefined;

			if (paymentMethod === 'CreditCard') {
				if (!chargifyForm.isReady) {
					toast.error('Payment form is not ready. Please wait and try again.');
					return;
				}
				toast.info('Processing payment information...');
				chargifyToken = await chargifyForm.getToken();
			}

			const result = await createSubscriptionMutation.mutateAsync({
				accountId: accountId || undefined,
				pricebookItemId: pricebookItemId || '',
				email: email.trim(),
				firstName: firstName.trim(),
				lastName: lastName.trim(),
				companyName: companyName.trim() || undefined,
				paymentMethod,
				creditCard: chargifyToken ? { chargifyToken } : undefined,
				netTerms: paymentMethod === 'Invoice' ? 0 : undefined,
			});

			// Store result in memory (hash history doesn't support state)
			setCollectPaymentResult({ result, accountId });

			// Navigate to confirmation page
			history.push('/tools/collect-payment/confirmation');
		} catch (error) {
			toast.error(error instanceof Error ? error.message : 'Failed to create subscription');
		}
	};

	const isLoading = pricebookItemQuery.isLoading;
	const isSubmitting = createSubscriptionMutation.isPending;

	if (isLoading) {
		return (
			<div className={css(styleSheet.loadingContainer)}>
				<LoadingSpinner type='small' />
				<span>Loading offer details...</span>
			</div>
		);
	}

	if (pricebookItemQuery.error || !pricebookItemQuery.data) {
		return (
			<div className={css(styleSheet.errorContainer)}>
				<h2>Error Loading Offer</h2>
				<p>Unable to load offer details for pricebook item: {pricebookItemId}</p>
				<Button variant='secondary' onClick={() => history.goBack()}>
					Go Back
				</Button>
			</div>
		);
	}

	const pricebookItem = pricebookItemQuery.data;

	// Calculate pricing breakdown using shared utility
	const pricing = calculatePricingBreakdown(pricebookItem);

	// Render a result row with optional monthly value
	const renderResultRow = (label: string, value: number, options?: { monthlyValue?: number }) => {
		if (value === 0 && options?.monthlyValue === undefined) return null;

		const monthlyDisplay =
			options?.monthlyValue !== undefined && options.monthlyValue > 0
				? `($${formatCurrency(options.monthlyValue)}/mo)`
				: '';

		return (
			<div className={css(styleSheet.breakdownRow)} key={label}>
				<span className={css(styleSheet.breakdownLabel)}>{label}</span>
				<span className={css(styleSheet.breakdownValue)}>
					{monthlyDisplay && <span className={css(styleSheet.monthlyNote)}>{monthlyDisplay}</span>}$
					{formatCurrency(value)}
				</span>
			</div>
		);
	};

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.layout)}>
				{/* Left: Form */}
				<form className={css(styleSheet.formSection)} onSubmit={handleSubmit}>
					<h2 className={css(styleSheet.sectionTitle)}>Customer Information</h2>

					{/* Email */}
					<div className={css(styleSheet.formGroup)}>
						<label className={css(styleSheet.label)} htmlFor='email'>
							Email *
						</label>
						<Input
							id='email'
							onChange={e => setEmail(e.target.value)}
							placeholder='customer@example.com'
							required
							styles={[styleSheet.input]}
							type='email'
							value={email}
						/>
					</div>

					{/* First Name */}
					<div className={css(styleSheet.formGroup)}>
						<label className={css(styleSheet.label)} htmlFor='firstName'>
							First Name *
						</label>
						<Input
							id='firstName'
							onChange={e => setFirstName(e.target.value)}
							placeholder='John'
							required
							styles={[styleSheet.input]}
							type='text'
							value={firstName}
						/>
					</div>

					{/* Last Name */}
					<div className={css(styleSheet.formGroup)}>
						<label className={css(styleSheet.label)} htmlFor='lastName'>
							Last Name *
						</label>
						<Input
							id='lastName'
							onChange={e => setLastName(e.target.value)}
							placeholder='Doe'
							required
							styles={[styleSheet.input]}
							type='text'
							value={lastName}
						/>
					</div>

					{/* Company Name (optional) */}
					<div className={css(styleSheet.formGroup)}>
						<label className={css(styleSheet.label)} htmlFor='companyName'>
							Company Name
						</label>
						<Input
							id='companyName'
							onChange={e => setCompanyName(e.target.value)}
							placeholder='Acme Corp (optional)'
							styles={[styleSheet.input]}
							type='text'
							value={companyName}
						/>
					</div>

					{/* Next button to proceed to payment */}
					{step === 'customer' && (
						<div className={css(styleSheet.buttonRow)}>
							<Button
								disabled={!email.trim() || !firstName.trim() || !lastName.trim()}
								onClick={() => setStep('payment')}
								type='button'
								variant='primary'
							>
								Next: Payment Method
							</Button>
						</div>
					)}

					{/* Payment Method Section - only shown after clicking Next */}
					{step === 'payment' && (
						<>
							<h2 className={css(styleSheet.sectionTitle)}>Payment Method</h2>

							{/* Payment Method Toggle */}
							<div className={css(styleSheet.paymentMethodToggle)}>
								<button
									className={css(
										styleSheet.paymentMethodButton,
										paymentMethod === 'CreditCard' && styleSheet.paymentMethodButtonActive
									)}
									onClick={() => setPaymentMethod('CreditCard')}
									type='button'
								>
									Credit Card
								</button>
								{allowInvoice && (
									<button
										className={css(
											styleSheet.paymentMethodButton,
											paymentMethod === 'Invoice' && styleSheet.paymentMethodButtonActive
										)}
										onClick={() => setPaymentMethod('Invoice')}
										type='button'
									>
										Invoice
									</button>
								)}
							</div>

							{/* Credit Card Fields (Chargify iframes) */}
							{paymentMethod === 'CreditCard' && (
								<div className={css(styleSheet.chargifyFormContainer)}>
									{chargifyForm.error && <div className={css(styleSheet.chargifyError)}>{chargifyForm.error}</div>}
									<div className={css(styleSheet.chargifyFormWrapper)}>
										{(chargifyCredentialsQuery.isLoading || chargifyForm.isLoading) && (
											<div className={css(styleSheet.chargifyLoadingOverlay)}>
												<LoadingSpinner type='small' />
												<span>Loading payment form...</span>
											</div>
										)}
										<div
											id='chargify-form'
											className={css(styleSheet.chargifyForm)}
											style={{
												opacity: chargifyCredentialsQuery.isLoading || chargifyForm.isLoading ? 0 : 1,
											}}
										>
											<div className={css(styleSheet.chargifyField)} id='chargify-first-name' />
											<div className={css(styleSheet.chargifyField)} id='chargify-last-name' />
											<div className={css(styleSheet.chargifyField)} id='chargify-city' />
											<div className={css(styleSheet.chargifyField)} id='chargify-state' />
											<div className={css(styleSheet.chargifyField)} id='chargify-zip' />
											<div className={css(styleSheet.chargifyField)} id='chargify-card-number' />
											<div className={css(styleSheet.chargifyField)} id='chargify-month' />
											<div className={css(styleSheet.chargifyField)} id='chargify-year' />
											<div className={css(styleSheet.chargifyField)} id='chargify-cvv' />
										</div>
									</div>
								</div>
							)}

							{/* Invoice Fields */}
							{paymentMethod === 'Invoice' && (
								<div className={css(styleSheet.invoiceSection)}>
									<p className={css(styleSheet.invoiceNote)}>
										{"An invoice will be sent to the customer's email address. Payment is due upon receipt."}
									</p>
								</div>
							)}

							{/* Submit Button */}
							<div className={css(styleSheet.buttonRow)}>
								<Button onClick={() => setStep('customer')} type='button' variant='secondary'>
									Back
								</Button>
								<Button
									disabled={isSubmitting || (paymentMethod === 'CreditCard' && !chargifyForm.isReady)}
									isLoading={isSubmitting}
									type='submit'
									variant='success'
								>
									{paymentMethod === 'CreditCard' ? 'Submit Payment' : 'Create Invoice'}
								</Button>
							</div>
						</>
					)}
				</form>

				{/* Right: Plan Summary */}
				<div className={css(styleSheet.summarySection)}>
					<h2 className={css(styleSheet.sectionTitle)}>Plan Summary</h2>

					<div className={css(styleSheet.summaryContent)}>
						{/* Plan Info */}
						<div className={css(styleSheet.planInfo)}>
							<div className={css(styleSheet.planName)}>{pricebookItem.productName}</div>
							<div className={css(styleSheet.planDetails)}>
								{5 + pricebookItem.additionalUsers} Users •{' '}
								{pricebookItem.productBillingCycle === 12
									? 'Annual'
									: pricebookItem.productBillingCycle === 3
										? 'Quarterly'
										: 'Monthly'}{' '}
								Billing
							</div>
						</div>

						{/* Pricing Breakdown */}
						<div className={css(styleSheet.breakdown)}>
							{renderResultRow('Base Price:', pricing.basePrice, { monthlyValue: pricing.basePriceMRR })}
							{pricing.extraUserCost > 0 &&
								renderResultRow('Extra User Cost:', pricing.extraUserCost, { monthlyValue: pricing.extraUserCostMRR })}
							{renderResultRow('Onboarding Fee:', pricing.onboardingFee)}
							{pricebookItem.hasFacebookAdsPremium &&
								renderResultRow('Meta Ads Premium Fee:', pricing.facebookAdsFee, {
									monthlyValue: pricing.facebookAdsMRR,
								})}
							{pricebookItem.hasGoogleLocalServiceAds &&
								renderResultRow('Google LSA Fee:', pricing.googleLSAFee, { monthlyValue: pricing.googleLSAMRR })}
							{pricebookItem.hasHandwrittenCards &&
								renderResultRow('$500 Handwritten Card Credit:', pricing.handwrittenCardsFee, {
									monthlyValue: pricing.handwrittenCardsMRR,
								})}
							{renderResultRow('Subtotal:', pricing.subtotal)}
							{pricing.discountAmount > 0 &&
								renderResultRow(`Discount (${pricing.discountPercentage}%):`, pricing.discountAmount)}
							<div className={css(styleSheet.breakdownRow)}>
								<span className={css(styleSheet.breakdownLabel)}>Tax:</span>
								<span className={css(styleSheet.breakdownValue)}>
									<TaxEstimateDisplay
										taxAmount={taxEstimate?.taxAmount ?? null}
										onCalculateClick={() => setIsTaxModalOpen(true)}
										onEditClick={() => setIsTaxModalOpen(true)}
										formatCurrency={formatCurrency}
									/>
								</span>
							</div>
						</div>

						{/* Amount Due Today */}
						<div className={css(styleSheet.totalRow)}>
							<span>Amount Due Today:</span>
							<span>${formatCurrency(pricebookItem.totalAmountBilledToday + (taxEstimate?.taxAmount ?? 0))}</span>
						</div>

						{/* Split Billing Note */}
						{pricebookItem.isSplitBilling && (
							<div className={css(styleSheet.splitBillingNote)}>
								The final installment payment of ${formatCurrency(pricebookItem.totalAmountBilledToday)} will be billed
								in 60 days.
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Tax Estimate Modal */}
			<TaxEstimateModal
				isOpen={isTaxModalOpen}
				onRequestClose={() => setIsTaxModalOpen(false)}
				pricebookItemId={pricebookItemId}
				initialData={taxEstimate ?? undefined}
				onSuccess={setTaxEstimate}
			/>
		</div>
	);
}
